import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contacts, isApiError } from '@repletoryreach/sdk-api';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import type { Translate } from '../../../i18n/I18nProvider';
import { formatDate } from '../../../lib/format';
import { useIsOrgAdmin } from '../../../app/components/RoleGate';
import { FIELDS_KEY, useContactFields } from './tabs/FieldsTab';

/*
 * A contact's details are never changed silently (owner's decision, 4 October
 * 2026). On 3 October a flow's Update contact step replaced the owner's own
 * name "Stanley" with "Stan", typed in a chat, and kept nothing. The contacts
 * service now records every change with what was there before; this file is
 * how the team sees those changes on the contact and undoes one.
 */

export const changesKey = (contactId: string) => ['contact', contactId, 'changes'] as const;

/** The contact's change history, newest first. Fetched for the open drawer only. */
export function useContactChanges(contactId: string | null) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: changesKey(contactId ?? ''),
    queryFn: () => contacts.listContactChanges(contactId!),
    enabled: !!contactId
  });
  /*
   * A flow that replaced a name also created the "Previous name" field the
   * first time it was needed, and the field list is cached for a minute; so a
   * history with a kept value refreshes the definitions, or the Fields tab
   * would not show the field the old value is in.
   */
  const keptPrevious = (q.data?.data ?? []).some((c) => c.outcome === 'changed' && (c.field === 'name' || c.field === 'email' || c.field === 'locale'));
  useEffect(() => {
    if (keptPrevious) qc.invalidateQueries({ queryKey: FIELDS_KEY });
  }, [keptPrevious, q.dataUpdatedAt, qc]);
  return q;
}

/** "Name", "Email address", "Language", or a custom field's own label. */
export function fieldLabel(t: Translate, field: string, defs: contacts.ContactFieldDefinition[]): string {
  if (field === 'name' || field === 'email' || field === 'locale') return t(`contacts.changes.field.${field}`);
  const key = field.startsWith('field:') ? field.slice(6) : field;
  return defs.find((d) => d.key === key)?.label ?? key;
}

/** The field as it reads mid-sentence: "their name", "hul naam". A custom field keeps its label. */
function fieldNoun(t: Translate, field: string, defs: contacts.ContactFieldDefinition[]): string {
  if (field === 'name' || field === 'email' || field === 'locale') return t(`contacts.changes.noun.${field}`);
  return fieldLabel(t, field, defs);
}

/** The flow's name: `source_ref` reads "Knowledge base assistant (run 01a1...)". */
export function flowName(sourceRef?: string | null): string | null {
  if (!sourceRef) return null;
  const cut = sourceRef.indexOf(' (run');
  const name = (cut >= 0 ? sourceRef.slice(0, cut) : sourceRef).trim();
  return name || null;
}

/** A person's address, or nothing for a bare id (a token without an email claim). */
function personOf(createdBy?: string | null): string | null {
  return createdBy && createdBy.includes('@') ? createdBy : null;
}

/** Who or what made the change: a flow's name, a person, an integration, or "Undo by ...". */
export function whoOf(t: Translate, c: contacts.ContactChange): string {
  switch (c.source) {
    case 'flow': return flowName(c.source_ref) ?? t('contacts.changes.who.flow');
    case 'api': return t('contacts.changes.who.api');
    case 'undo': return t('contacts.changes.who.undo', { person: personOf(c.created_by) ?? t('contacts.changes.who.person') });
    default: return personOf(c.created_by) ?? t('contacts.changes.who.person');
  }
}

/** A value as shown: a blank one says so, so a sentence never reads "from  to Stan". */
export function shown(t: Translate, v?: string | null): string {
  return v && v.trim() ? v : t('contacts.changes.emptyValue');
}

/** One change in plain words, for the banner. */
export function sentenceOf(t: Translate, c: contacts.ContactChange, defs: contacts.ContactFieldDefinition[]): string {
  if (c.outcome === 'kept') {
    return t('contacts.changes.line.kept', { noun: fieldNoun(t, c.field, defs), new: shown(t, c.new_value), old: shown(t, c.old_value) });
  }
  const vars = { field: fieldLabel(t, c.field, defs), old: shown(t, c.old_value), new: shown(t, c.new_value), date: formatDate(c.occurred_at), who: whoOf(t, c) };
  const head = c.new_value && c.new_value.trim() ? t('contacts.changes.line.changed', vars) : t('contacts.changes.line.cleared', vars);
  if (c.confirmed_by === 'customer') return `${head} ${t('contacts.changes.line.confirmed')}`;
  // Only a flow could have asked; a person in the console made the change themselves.
  if (c.source === 'flow') return `${head} ${t('contacts.changes.line.notAsked')}`;
  return head;
}

/**
 * Whether a row can be undone from here: a replacement of a value that was
 * there, the latest change to its field (an older one would only meet
 * CHANGED_SINCE), not itself an undo, and an owner or admin looking. The
 * service decides in the end; this only keeps buttons off rows that would fail.
 */
export function undoable(c: contacts.ContactChange, all: contacts.ContactChange[], isAdmin: boolean): boolean {
  if (!isAdmin || c.outcome !== 'changed' || c.source === 'undo' || !c.old_value || !c.old_value.trim()) return false;
  const latest = all.find((x) => x.field === c.field && x.outcome === 'changed');
  return latest?.id === c.id;
}

/** Undo one change, with the service's refusals said in words. */
export function useUndoChange(contactId: string, onUndone?: (field: string, value: string | null) => void) {
  const t = useT();
  const qc = useQueryClient();
  const { defs } = useContactFields();
  const [refusal, setRefusal] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: (changeId: string) => contacts.undoContactChange(contactId, changeId),
    onMutate: () => setRefusal(null),
    onSuccess: (r) => {
      const { field, value } = r.data;
      notifySuccess(t('contacts.changes.undone', { field: fieldLabel(t, field, defs), value: shown(t, value) }), t('contacts.title'));
      qc.invalidateQueries({ queryKey: ['contact', contactId] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      onUndone?.(field, value);
    },
    onError: (e) => {
      if (!isApiError(e)) { notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('toast.saveFailed')); return; }
      if (e.code === 'CHANGED_SINCE') {
        // The service puts what the field holds now beside the code: `{ error: { code, message, current } }`.
        const body = e.body as { error?: { current?: string | null }; current?: string | null } | undefined;
        const current = body?.error?.current ?? body?.current;
        setRefusal(current && current.trim() ? t('contacts.changes.error.changedSince', { current }) : t('contacts.changes.error.changedSinceEmpty'));
      } else if (e.code === 'NOTHING_TO_UNDO') setRefusal(t('contacts.changes.error.nothing'));
      else if (e.code === 'CHANGE_NOT_FOUND') setRefusal(t('contacts.changes.error.notFound'));
      else if (e.code === 'ADMIN_ROLE_REQUIRED') setRefusal(t('contacts.changes.error.role'));
      else notifyError(e.message, t('toast.saveFailed'));
      // Whatever the reason, the history on screen may be out of date.
      qc.invalidateQueries({ queryKey: changesKey(contactId) });
    }
  });
  return { undo: m.mutate, pendingId: m.isPending ? m.variables : null, refusal, clearRefusal: () => setRefusal(null) };
}

/**
 * At the top of the Details tab: the changes a flow made that nobody on the
 * team has looked at yet, one sentence each, with Undo (owners and admins)
 * and Got it, which marks them all seen.
 */
export function ChangesBanner({ contactId, onUndone }: { contactId: string; onUndone: (field: string, value: string | null) => void }) {
  const t = useT();
  const qc = useQueryClient();
  const isAdmin = useIsOrgAdmin();
  const { defs } = useContactFields();
  const { data } = useContactChanges(contactId);
  const { undo, pendingId, refusal } = useUndoChange(contactId, onUndone);
  const review = useMutation({
    mutationFn: () => contacts.reviewContactChanges(contactId),
    onSuccess: () => qc.invalidateQueries({ queryKey: changesKey(contactId) }),
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('toast.saveFailed'))
  });

  const all = data?.data ?? [];
  const open = all.filter((c) => c.reviewed_at == null);
  if (open.length === 0) return null;

  return (
    <Alert color="warning" title={t('contacts.changes.banner.title')} className="rr-contact-changes__banner" data-testid="contact-changes-banner">
      <Stack gap="xs">
        {open.map((c) => (
          <div key={c.id} className="rr-contact-changes__line">
            <Text role="bodySmall" c="var(--rr-color-text)" style={{ flex: 1, minWidth: 0 }}>{sentenceOf(t, c, defs)}</Text>
            {undoable(c, all, isAdmin) && (
              <Button size="xs" variant="default" loading={pendingId === c.id} onClick={() => undo(c.id)}>
                {t('contacts.changes.undo')}
              </Button>
            )}
          </div>
        ))}
        {refusal && <div role="alert"><Text role="bodySmall" c="var(--rr-color-error)">{refusal}</Text></div>}
        <Text role="caption" c="var(--rr-color-text-muted)">{t('contacts.changes.banner.hint')}</Text>
        <Group justify="flex-end">
          <Button size="xs" loading={review.isPending} onClick={() => review.mutate()}>{t('contacts.changes.gotIt')}</Button>
        </Group>
      </Stack>
    </Alert>
  );
}
